import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { UrlLink } from "../../../../../shared/domains/BaseUrl";
import { OnboardingStatus } from "../../../../../shared/domains/onboarding/onboarding";
import { useOnboardingFile } from "../../../../../shared/domains/onboarding/use-onboarding";
import { Path } from "../../../../core/routing/path";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { PrimaryButton, WhitePrimaryButton } from "../../../common/buttons/primary-button";
import { Spinner } from "../../../common/spinner";
import { ChevronIcon } from "../../../common/svg/chevron";
import { theme } from "../../../styles/theme";
import EnrollmentLayout from "../shared/components/enrollment-layout";
import EnrollmentProgress from "../shared/components/enrollment-progress";
import EnrollmentStepForm from "../shared/components/enrollment-step-form";

export const OnboardingFileScreen = () => {
  const { push } = useHistory();
  const { formatMessage } = useIntl();
  const {
    error,
    erroredFields,
    needSaveBeforeSubmit,
    isUpdating,
    isSubmitting,
    onboardingFile,
    steps,
    canUpdate,
    canSubmit,
    currentStepIndex,
    currentStep,
    isLastStep,
    fillResponse,
    fillMultiImages,
    moveBackward,
    moveForward,
    submitOnboardingFile,
    areThereMissingRequiredFields,
  } = useOnboardingFile();

  useEffect(() => {
    if (
      onboardingFile?.status === OnboardingStatus.SUBMITTED ||
      onboardingFile?.status === OnboardingStatus.PENDING_VERIFICATION
    ) {
      push(Path.OnboardingFileSubmitted);
    }
  }, [onboardingFile]);

  const handleSubmitOnboardingFIle = async () => {
    const res = await submitOnboardingFile();

    if (res?.onboardingFile?.status === OnboardingStatus.EXTERNAL_CHECK_PENDING) {
      const clientJourneyUrl = res.onboardingFile.links.find((l: UrlLink) => l.rel === "clientJourneyUrl").href;
      clientJourneyUrl && window.location.assign(clientJourneyUrl);
    }
  };

  const handleMoveBackward = () => {
    moveBackward();
  };

  const hasFormErrors = erroredFields.length > 0 || areThereMissingRequiredFields(currentStep.inputs);

  return (
    <EnrollmentLayout>
      <EnrollmentProgress
        step={currentStepIndex}
        stepCount={steps?.length}
        label={currentStep?.label}
        onBack={currentStepIndex > 0 ? handleMoveBackward : undefined}
      />
      {!!currentStep && onboardingFile && (
        <>
          <EnrollmentStepFormWrapper>
            <EnrollmentStepForm
              disabled={!canUpdate || isUpdating}
              step={currentStep}
              onInputValueChange={fillResponse}
              onInputMultiImagesChange={fillMultiImages}
              onSubmit={moveForward}
              errorMessage={error}
              erroredFields={erroredFields}
              overrideActions={
                <StyledMainButtonContainer>
                  {isLastStep ? (
                    <>
                      {/* Saving is the quieter of the two: submitting is what closes the file. */}
                      <SaveLink type="submit" disabled={isUpdating || !canUpdate}>
                        {/* The label keeps its place while it is hidden, so the link does not jump. */}
                        <SaveLabel $hidden={isUpdating}>{formatMessage("onboardingScreen.save")}</SaveLabel>
                        {isUpdating && <SaveLoader size="16px" />}
                      </SaveLink>
                      <PrimaryButton
                        size="S"
                        type="button"
                        onClick={handleSubmitOnboardingFIle}
                        showSpinner={isSubmitting}
                        disabled={needSaveBeforeSubmit || !canSubmit || hasFormErrors}
                      >
                        {formatMessage("onboardingScreen.submit")}
                      </PrimaryButton>
                    </>
                  ) : (
                    /* Spinning already blocks the pointer, so the button is not greyed out too. */
                    <NextButton size="S" type="submit" showSpinner={isUpdating} disabled={!canUpdate}>
                      {formatMessage("onboardingScreen.next")}
                      <NextChevron width={10} height={10} />
                    </NextButton>
                  )}
                </StyledMainButtonContainer>
              }
            />
          </EnrollmentStepFormWrapper>
        </>
      )}
    </EnrollmentLayout>
  );
};

const EnrollmentStepFormWrapper = styled.div`
  flex: 1;
  width: 100%;
  max-width: 600px;
`;

const StyledMainButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

/* Outlined and chevroned, the way the mockup carries the step forward. */
const NextButton = styled(WhitePrimaryButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};
`;

const NextChevron = styled(ChevronIcon)`
  fill: ${theme.colors.blue[700]};
`;

const SaveLink = styled(UnstyledButton)`
  position: relative;
  font-size: 0.8125rem;
  color: ${theme.colors.blue[700]};
  text-decoration: underline;

  &:disabled {
    opacity: 0.5;
  }
`;

const SaveLabel = styled.span<{ $hidden: boolean }>`
  opacity: ${(props) => (props.$hidden ? 0 : 1)};
`;

/* UnstyledButton drops showSpinner, so the save link overlays its own. */
const SaveLoader = styled(Spinner)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
